import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Loader2, User, Mail, Calendar, Crown, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import TrustBadge from '@/components/TrustBadge';
import TrustScoreCard from '@/components/TrustScoreCard';
import DeleteMyDataButton from '@/components/DeleteMyDataButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ProfilePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [user, setUser] = useState(null);
  const [membershipStatus, setMembershipStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingPayment, setCheckingPayment] = useState(false);

  useEffect(() => {
    loadProfile();
    
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      checkMembershipPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const loadProfile = async () => {
    try {
      const [userRes, membershipRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/memberships/status`, { withCredentials: true })
      ]);
      setUser(userRes.data);
      setMembershipStatus(membershipRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Load profile error:', error);
      setLoading(false);
    }
  };

  const checkMembershipPaymentStatus = async (sessionId) => {
    setCheckingPayment(true);
    let attempts = 0;
    const maxAttempts = 5;

    const poll = async () => {
      try {
        const response = await axios.get(
          `${BACKEND_URL}/api/memberships/checkout/status/${sessionId}`,
          { withCredentials: true }
        );

        if (response.data.payment_status === 'paid') {
          toast.success('Membership activated successfully!');
          navigate('/profile', { replace: true });
          loadProfile();
          return;
        }

        if (attempts < maxAttempts) {
          attempts++;
          setTimeout(poll, 2000);
        } else {
          toast.info('Payment processing... Please check back shortly.');
          setCheckingPayment(false);
        }
      } catch (error) {
        console.error('Payment check error:', error);
        setCheckingPayment(false);
      }
    };

    poll();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-12 h-12 text-indigo-600 animate-spin" />
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="flex items-center justify-center h-[80vh]">
          <p className="text-stone-600">Unable to load profile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />

      <main className="max-w-4xl mx-auto px-6 py-8">
        {checkingPayment && (
          <Card className="mb-6 border-indigo-200 bg-indigo-50">
            <CardContent className="pt-6">
              <div className="flex items-center">
                <Loader2 className="w-5 h-5 text-indigo-600 animate-spin mr-3" />
                <span className="text-indigo-900">Verifying your membership payment...</span>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-stone-900 mb-2">Profile</h1>
          <p className="text-stone-600">Manage your account information</p>
        </div>

        <Card className="border-stone-200 mb-6">
          <CardHeader>
            <CardTitle>Personal Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center space-x-6 mb-6">
              <Avatar className="w-24 h-24">
                <AvatarImage src={user.picture} />
                <AvatarFallback className="bg-indigo-100 text-indigo-600 text-2xl">
                  {user.name?.charAt(0) || 'U'}
                </AvatarFallback>
              </Avatar>
              <div>
                <h2 className="text-2xl font-bold text-stone-900" data-testid="profile-name">{user.name}</h2>
                <p className="text-stone-600" data-testid="profile-email">{user.email}</p>
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <TrustBadge size="md" data-testid="profile-trust-badge" />
                  {membershipStatus && (
                    membershipStatus.tier === 'free' ? (
                      <Badge variant="outline" className="bg-stone-50">Free Plan</Badge>
                    ) : (
                      <Badge className={
                        membershipStatus.tier === 'premium' 
                          ? 'bg-amber-100 text-amber-700 hover:bg-amber-100' 
                          : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-100'
                      }>
                        {membershipStatus.tier === 'premium' ? <Crown className="w-3 h-3 mr-1" /> : <Sparkles className="w-3 h-3 mr-1" />}
                        {membershipStatus.tier.charAt(0).toUpperCase() + membershipStatus.tier.slice(1)} Member
                      </Badge>
                    )
                  )}
                </div>
              </div>
            </div>

            {/* Trust score breakdown card */}
            <TrustScoreCard />

            {/* Right-to-delete — Plaid-required standalone entrypoint */}
            <div className="mt-6" data-testid="profile-delete-my-data-wrapper">
              <DeleteMyDataButton />
            </div>

            <div className="space-y-4">
              <div className="flex items-center p-4 bg-stone-50 rounded-xl">
                <Mail className="w-5 h-5 text-stone-600 mr-4" />
                <div>
                  <div className="text-sm text-stone-600">Email Address</div>
                  <div className="font-medium text-stone-900">{user.email}</div>
                </div>
              </div>
              <div className="flex items-center p-4 bg-stone-50 rounded-xl">
                <User className="w-5 h-5 text-stone-600 mr-4" />
                <div>
                  <div className="text-sm text-stone-600">User ID</div>
                  <div className="font-medium text-stone-900">{user.user_id}</div>
                </div>
              </div>
              <div className="flex items-center p-4 bg-stone-50 rounded-xl">
                <Calendar className="w-5 h-5 text-stone-600 mr-4" />
                <div>
                  <div className="text-sm text-stone-600">Member Since</div>
                  <div className="font-medium text-stone-900">
                    {new Date(user.created_at).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {membershipStatus && (
          <Card className="border-stone-200 mb-6">
            <CardHeader>
              <CardTitle>Membership Status</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between p-6 bg-gradient-to-r from-indigo-50 to-amber-50 rounded-xl">
                <div>
                  <div className="text-sm text-stone-600 mb-1">Current Plan</div>
                  <div className="text-2xl font-bold text-stone-900 capitalize">
                    {membershipStatus.tier}
                  </div>
                  {membershipStatus.expires_at && (
                    <div className="text-sm text-stone-600 mt-2">
                      {membershipStatus.is_active ? (
                        <>Expires: {new Date(membershipStatus.expires_at).toLocaleDateString()}</>
                      ) : (
                        <span className="text-red-600">Expired</span>
                      )}
                    </div>
                  )}
                </div>
                <div>
                  {membershipStatus.tier === 'free' || !membershipStatus.is_active ? (
                    <Button
                      data-testid="upgrade-membership-button"
                      onClick={() => navigate('/membership')}
                      className="rounded-full bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Crown className="mr-2 w-4 h-4" />
                      Upgrade Now
                    </Button>
                  ) : (
                    <div className="text-right">
                      <div className="text-3xl">{membershipStatus.tier === 'premium' ? '👑' : '⭐'}</div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <Card className="border-stone-200">
          <CardHeader>
            <CardTitle>Savings Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-between p-6 bg-gradient-to-r from-indigo-50 to-amber-50 rounded-xl">
              <div>
                <div className="text-sm text-stone-600 mb-1">Total Savings</div>
                <div className="text-3xl font-bold text-stone-900" data-testid="profile-total-savings">
                  ${user.total_savings?.toFixed(2) || '0.00'}
                </div>
              </div>
              <div className="text-4xl">💰</div>
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}